/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { signOut } from "aws-amplify/auth";
import { type FC } from "react";
import { Button } from "../ui/button";
import { theme } from "../../styles/theme/theme";

const s = {
  page: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
    gap: ${theme.spacing.md};
    padding: ${theme.spacing.lg};
    text-align: center;
    background-color: ${theme.colors.background};
  `,
  heading: css`
    margin: 0;
    font-size: ${theme.typography.fontSize.lg};
    color: ${theme.colors.foreground};
  `,
  body: css`
    margin: 0;
    max-width: 34rem;
    font-size: ${theme.typography.fontSize.sm};
    line-height: 1.5;
    color: ${theme.colors.mutedForeground};
  `,
};

/**
 * Shown INSTEAD of the app when a signed-in user holds no role at all.
 *
 * Authentication succeeded — Cognito knows them — but every role has been revoked, so
 * `custom:role` is the empty string and the RBAC set is `[]`. There is deliberately no
 * fallback role: emptying a user's set used to collapse to ADMIN, which promoted the
 * very people an operator had just restricted (see `primaryRoleFor` in the gateway).
 *
 * So the app renders nothing rather than guessing. A roleless user would otherwise land
 * on a shell whose every query 403s, which reads as breakage instead of "ask for access".
 *
 * Sign out is offered because it is the only action available to them — without it a
 * roleless session is a dead end, and switching accounts means clearing storage by hand.
 */
export const NoRoleAssigned: FC = () => (
  <div css={s.page}>
    <h1 css={s.heading}>No role found for your user</h1>
    <p css={s.body}>
      Please contact your administrator to request a role assignment. You will be able to
      sign in normally once a role has been granted.
    </p>
    <Button
      type="button"
      variant="tertiaryOutline"
      onClick={() => {
        void signOut();
      }}
    >
      Sign out
    </Button>
  </div>
);
